import React, { useState } from 'react';
import { Button, Menu, MenuItem, Box, Typography, Collapse, IconButton } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import { useTheme } from '../../ThemeContext';

/**
 * 🎛️ FilterDropdown - Reusable Multi-Category Filter Component
 * 
 * A flexible dropdown filter component that supports multiple filter categories
 * with customizable options and theme-aware styling.
 * 
 * @param {Object} props
 * @param {Array} props.filterGroups - Array of filter groups: 
 *   [{ name: 'Status', key: 'status', options: [{ label: 'Active', value: 'active' }] }]
 * @param {Object} props.selectedFilters - Currently selected filters: { status: 'active', branch: 'medical' }
 * @param {Function} props.onFiltersChange - Callback when filters change (filters) => {}
 * @param {string} props.placeholder - Button placeholder text
 * @param {Object} props.sx - Additional styling
 * @param {boolean} props.showIcon - Show filter icon (default: true)
 * @param {boolean} props.showApplyButton - Show apply/cancel buttons (default: true)
 * @param {...Object} otherProps - Other props passed to root Box
 */
const FilterDropdown = ({
  filterGroups = [],
  selectedFilters = {},
  onFiltersChange = () => {},
  placeholder = "Filter",
  sx = {},
  showIcon = true,
  showApplyButton = true,
  ...otherProps
}) => {
  const { themeMode } = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempFilters, setTempFilters] = useState(selectedFilters);
  const [expandedGroups, setExpandedGroups] = useState({ status: true, facility: false }); // Status expanded by default, facility collapsed
  const open = Boolean(anchorEl);

  // Theme-aware colors
  const colors = {
    buttonBg: themeMode === 'dark' ? '#3f3c57' : '#f5f5f5',
    buttonBorder: themeMode === 'dark' ? '#252144' : '#d0d0d0',
    buttonText: themeMode === 'dark' ? '#ffffff' : '#000000',
    buttonHover: themeMode === 'dark' ? '#4a4763' : '#e8e8e8',
    menuBg: themeMode === 'dark' ? '#3f3c57' : '#ffffff',
    menuBorder: themeMode === 'dark' ? '#252144' : '#e0e0e0',
    menuItemHover: themeMode === 'dark' ? '#4a4763' : '#f5f5f5',
    menuItemText: themeMode === 'dark' ? '#ffffff' : '#000000',
    selectedBg: themeMode === 'dark' ? '#1e3a8a' : '#e3f2fd',
    selectedText: themeMode === 'dark' ? '#93c5fd' : '#1976d2',
  };

  const handleClick = (event) => {
    setTempFilters(selectedFilters); // Reset temp filters to current selection
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTempFilters(selectedFilters); // Reset temp filters on close
  };

  const handleFilterChange = (filterKey, value) => {
    setTempFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleApplyFilters = () => {
    onFiltersChange(tempFilters);
    setAnchorEl(null);
  };

  const handleCancelFilters = () => {
    setTempFilters(selectedFilters);
    setAnchorEl(null);
  };

  const clearAllFilters = () => {
    const clearedFilters = {};
    filterGroups.forEach(group => {
      clearedFilters[group.key] = null;
    });
    setTempFilters(clearedFilters);
  };

  // Get display text for button
  const getDisplayText = () => {
    const activeFilters = Object.values(selectedFilters).filter(value => value !== null && value !== undefined && value !== '');
    
    if (activeFilters.length === 0) {
      return placeholder;
    }
    
    if (activeFilters.length === 1) {
      // Find the filter group and option to show the label
      for (const group of filterGroups) {
        const selectedValue = selectedFilters[group.key];
        if (selectedValue) {
          const option = group.options.find(opt => opt.value === selectedValue);
          return option ? option.label : selectedValue;
        }
      }
    }
    
    return `${activeFilters.length} filters applied`;
  };

  // Check if option is selected for a specific filter group
  const isSelected = (filterKey, value) => {
    return tempFilters[filterKey] === value;
  };

  // Count active filters
  const getActiveFiltersCount = () => {
    return Object.values(selectedFilters).filter(value => value !== null && value !== undefined && value !== '').length;
  };

  return (
    <Box sx={{ display: 'inline-block', ...sx }} {...otherProps}>
      <Button
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        startIcon={showIcon ? <FilterListIcon /> : null}
        sx={{
          backgroundColor: colors.buttonBg,
          border: `1px solid ${colors.buttonBorder}`,
          color: colors.buttonText,
          textTransform: 'none',
          borderRadius: '6px',
          px: 2,
          py: 1,
          minWidth: '120px',
          justifyContent: 'space-between',
          '&:hover': {
            backgroundColor: colors.buttonHover,
            border: `1px solid ${colors.buttonBorder}`,
          },
          '&:focus': {
            outline: 'none',
            boxShadow: `0 0 0 2px ${themeMode === 'dark' ? '#3b82f6' : '#1976d2'}40`,
          },
          '& .MuiButton-startIcon': {
            marginRight: '8px',
            '& svg': {
              fontSize: '18px',
            },
          },
          '& .MuiButton-endIcon': {
            marginLeft: 'auto',
            '& svg': {
              fontSize: '18px',
            },
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: colors.buttonText,
            fontWeight: 500,
            fontSize: '14px',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            flex: 1,
            textAlign: 'left',
          }}
        >
          {getDisplayText()}
        </Typography>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'filter-button',
        }}
        PaperProps={{
          sx: {
            backgroundColor: colors.menuBg,
            border: `1px solid ${colors.menuBorder}`,
            borderRadius: '8px',
            boxShadow: themeMode === 'dark' 
              ? '0 4px 20px rgba(0, 0, 0, 0.4)' 
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
            minWidth: '280px',
            maxWidth: '400px',
            mt: 0.5,
            maxHeight: '450px', // Reduced to account for collapsible content
            overflow: 'visible', // Changed to visible to allow proper collapse animation
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {/* Filter Groups */}
        <Box sx={{ maxHeight: '350px', overflowY: 'auto' }}>
          {filterGroups.map((group, groupIndex) => {
            const isExpanded = expandedGroups[group.key] ?? true;
            const isFacilityGroup = group.key === 'facility';
            
            return (
              <Box key={group.key}>
                {/* Group Header */}
                <Box 
                  sx={{ 
                    px: 2, 
                    py: 1.5, 
                    borderBottom: `1px solid ${colors.menuBorder}`,
                    backgroundColor: themeMode === 'dark' ? '#2a2640' : '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: isFacilityGroup ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (isFacilityGroup) {
                      setExpandedGroups(prev => ({
                        ...prev,
                        [group.key]: !prev[group.key]
                      }));
                    }
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: colors.menuItemText,
                      fontWeight: 600,
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {group.name}
                    {isFacilityGroup && group.options.length > 1 && (
                      <Typography component="span" sx={{ ml: 1, fontSize: '11px', opacity: 0.7 }}>
                        ({group.options.length - 1} facilities)
                      </Typography>
                    )}
                  </Typography>
                  {isFacilityGroup && (
                    <IconButton 
                      size="small" 
                      sx={{ p: 0.5, color: colors.menuItemText }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedGroups(prev => ({
                          ...prev,
                          [group.key]: !prev[group.key]
                        }));
                      }}
                    >
                      {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  )}
                </Box>

                {/* Group Options */}
                <Collapse in={isExpanded} timeout={200}>
                  <Box sx={{ maxHeight: isFacilityGroup ? '200px' : 'none', overflowY: isFacilityGroup ? 'auto' : 'visible' }}>
            {group.options.map((option) => (
              <MenuItem
                key={`${group.key}-${option.value}`}
                onClick={() => handleFilterChange(group.key, option.value)}
                sx={{
                  color: colors.menuItemText,
                  py: 1,
                  px: 2,
                  minHeight: '36px',
                  '&:hover': {
                    backgroundColor: colors.menuItemHover,
                  },
                  ...(isSelected(group.key, option.value) && {
                    backgroundColor: colors.selectedBg,
                    color: colors.selectedText,
                    '&:hover': {
                      backgroundColor: colors.selectedBg,
                    },
                  }),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      fontSize: '14px',
                      fontWeight: isSelected(group.key, option.value) ? 600 : 400,
                    }}
                  >
                    {option.label}
                  </Typography>
                  {isSelected(group.key, option.value) && (
                    <CheckIcon
                      sx={{
                        fontSize: '16px',
                        color: colors.selectedText,
                        ml: 1,
                      }}
                    />
                  )}
                </Box>
              </MenuItem>
                    ))}
                  </Box>
                </Collapse>

                {/* Divider between groups (except last) */}
                {groupIndex < filterGroups.length - 1 && (
                  <Box sx={{ 
                    height: '1px', 
                    backgroundColor: colors.menuBorder,
                    mx: 1,
                    my: 1,
                  }} />
                )}
              </Box>
            );
          })}
        </Box>

        {/* Action Buttons */}
        {showApplyButton && (
          <Box sx={{ 
            p: 2, 
            borderTop: `1px solid ${colors.menuBorder}`,
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
          }}>
            <Button
              variant="text"
              size="small"
              onClick={clearAllFilters}
              sx={{
                color: colors.menuItemText,
                textTransform: 'none',
                fontSize: '12px',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: colors.menuItemHover,
                },
              }}
            >
              Clear All
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelFilters}
                sx={{
                  color: colors.menuItemText,
                  borderColor: colors.buttonBorder,
                  textTransform: 'none',
                  fontSize: '12px',
                  minWidth: '60px',
                  '&:hover': {
                    backgroundColor: colors.menuItemHover,
                    borderColor: colors.buttonBorder,
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyFilters}
                sx={{
                  backgroundColor: '#e63e2b',
                  color: '#ffffff',
                  textTransform: 'none',
                  fontSize: '12px',
                  minWidth: '60px',
                  '&:hover': {
                    backgroundColor: '#d32f2f',
                  },
                }}
              >
                Apply Filters
              </Button>
            </Box>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default FilterDropdown;
